import { queryClient } from "./queryClient";
import { resetIdentity } from "./analytics";

const ACCESS_KEY = "rr_access_token";
const REFRESH_KEY = "rr_refresh_token";
const BASE = process.env.REACT_APP_BACKEND_URL;
const VIEW_AS_KEY = "rr_view_as_session";

function _activeViewAsFor(path) {
  const raw = sessionStorage.getItem(VIEW_AS_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw);
    if (new Date(session.expires_at).getTime() <= Date.now()) return null;
    // Only ever attached to a call for the exact Workspace it was
    // started against — never leaked onto an unrelated request.
    if (!path.includes(`/workspaces/${session.workspace_id}/`)) return null;
    return session;
  } catch {
    return null;
  }
}

export function getTokens() {
  return {
    access: localStorage.getItem(ACCESS_KEY),
    refresh: localStorage.getItem(REFRESH_KEY),
  };
}

export function setTokens(access, refresh) {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

/**
 * Fetch wrapper that attaches the Rehearsal Room bearer token and
 * transparently retries once via /api/auth/refresh on a 401. portal_role
 * itself is never read from anything client-side — this only carries the
 * token, the server always re-derives the role from the DB.
 */
export async function rrFetch(path, options = {}) {
  const { access } = getTokens();
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const viewAs = _activeViewAsFor(path);
  const doFetch = (token) =>
    fetch(`${BASE}${path}`, {
      ...options,
      headers: {
        ...(options.headers || {}),
        // FormData needs the browser's own multipart boundary — never
        // force application/json over it (would break upload parsing).
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(viewAs ? { "X-View-As-Session": viewAs.vas_id, "X-View-As-Token": viewAs.session_token } : {}),
      },
    });

  const first = await doFetch(access);
  if (first.status !== 401) return first;

  const { refresh } = getTokens();
  if (!refresh) return first;

  const refreshRes = await fetch(`${BASE}/api/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refresh }),
  });
  if (!refreshRes.ok) {
    clearTokens();
    return first;
  }
  const data = await refreshRes.json();
  setTokens(data.access_token, data.refresh_token);
  return doFetch(data.access_token);
}

export async function rrLogout() {
  const { refresh } = getTokens();
  if (refresh) {
    try {
      await fetch(`${BASE}/api/auth/logout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: refresh }),
      });
    } catch {
      // logout is best-effort client-side regardless of network outcome
    }
  }
  clearTokens();
  // Fix (Prompt 28 hardening): a second person logging in on the same
  // browser without a full page reload would otherwise still see the
  // previous person's cached React Query data (coach-scoped workbench
  // queries have no per-user key at all) for up to `staleTime`.
  queryClient.clear();
  // Identity-stitching fix (2026-09): clears PostHog's local identity
  // too, so a second person logging into (or a public visitor using)
  // this SAME browser after this logout never inherits the previous
  // person's identified PostHog identity — a shared-device analytics
  // hygiene fix only, no auth/session logic changed above this line.
  resetIdentity();
}
